import type { OutreachSource } from "../hooks/useEmployerOutreachSource";

import { OutreachType } from "../hooks/useEmployerOutreachUIConfig";

import {
  EmployerOutreachAction,
  EmployerOutreachSource,
  log as logEmployerOutreachInteraction,
  AvroOptional,
} from "@indeed/signals-schema/com/indeed/signals/schema/viewjob/EmployerOutreachInteractionNew";

/**
 * Lookup table for mapping outreach source to signal schema enum
 */
const OUTREACH_SOURCE_MAP: Record<string, EmployerOutreachSource> = {
  AUTOSOURCER: EmployerOutreachSource.AUTOSOURCER,
  SMART_SOURCING: EmployerOutreachSource.SMART_SOURCING,
  PREMIUM_MATCH_CANDIDATES: EmployerOutreachSource.PREMIUM_MATCH_CANDIDATES,
};

/**
 * Maps outreach source to the signal schema enum
 */
function mapOutreachSourceToEnum(outreachSource?: OutreachSource): {
  EmployerOutreachSource: EmployerOutreachSource;
} {
  const sourceString = outreachSource ?? "";

  const mappedSource =
    OUTREACH_SOURCE_MAP[sourceString.toUpperCase()] || EmployerOutreachSource.UNKNOWN;

  return { EmployerOutreachSource: mappedSource };
}

/**
 * Logs when a user declines an employer outreach job
 */
export function logEmployerOutreachDecline(params: {
  employerOutreachSource?: OutreachSource;
  accountKey?: string;
  ctk?: string;
  jobKey?: string;
  mobtk?: string;
  isError?: boolean;
}): void {
  const { employerOutreachSource, accountKey, ctk, jobKey, mobtk, isError = false } = params;

  logEmployerOutreachInteraction({
    employerOutreachAction: { EmployerOutreachAction: EmployerOutreachAction.DECLINE },
    employerOutreachSource: mapOutreachSourceToEnum(employerOutreachSource),
    accountKey: AvroOptional.string(accountKey),
    ctk: AvroOptional.string(ctk),
    jobkKey: AvroOptional.string(jobKey),
    mobtk: AvroOptional.string(mobtk),
    isError: AvroOptional.boolean(isError),
  });
}

/**
 * Logs when a user submits a decline reason for an employer outreach job
 */
export function logEmployerOutreachSubmitDeclineReason(params: {
  employerOutreachSource?: OutreachSource;
  accountKey?: string;
  ctk?: string;
  jobKey?: string;
  mobtk?: string;
  isError?: boolean;
}): void {
  const { employerOutreachSource, accountKey, ctk, jobKey, mobtk, isError = false } = params;

  logEmployerOutreachInteraction({
    employerOutreachAction: {
      EmployerOutreachAction: EmployerOutreachAction.SUBMIT_DECLINE_REASON,
    },
    employerOutreachSource: mapOutreachSourceToEnum(employerOutreachSource),
    accountKey: AvroOptional.string(accountKey),
    ctk: AvroOptional.string(ctk),
    jobkKey: AvroOptional.string(jobKey),
    mobtk: AvroOptional.string(mobtk),
    isError: AvroOptional.boolean(isError),
  });
}

/**
 * Logs when a user accepts (applies to) an employer outreach job
 */
export function logEmployerOutreachApply(params: {
  employerOutreachSource?: OutreachSource;
  outreachType?: OutreachType;
  accountKey?: string;
  ctk?: string;
  jobKey?: string;
  mobtk?: string;
  isError?: boolean;
}): void {
  const {
    employerOutreachSource,
    outreachType,
    accountKey,
    ctk,
    jobKey,
    mobtk,
    isError = false,
  } = params;

  // Determine if this is a fast apply based on outreach type
  const isFastApply = outreachType === OutreachType.FAST_APPLY;
  const action = isFastApply ? EmployerOutreachAction.FAST_APPLY : EmployerOutreachAction.APPLY;

  logEmployerOutreachInteraction({
    employerOutreachAction: { EmployerOutreachAction: action },
    employerOutreachSource: mapOutreachSourceToEnum(employerOutreachSource),
    accountKey: AvroOptional.string(accountKey),
    ctk: AvroOptional.string(ctk),
    jobkKey: AvroOptional.string(jobKey),
    mobtk: AvroOptional.string(mobtk),
    isError: AvroOptional.boolean(isError),
  });
}

/**
 * Logs when a user completes an apply for an employer outreach job
 */
export function logEmployerOutreachCompleteApply(params: {
  employerOutreachSource?: OutreachSource;
  accountKey?: string;
  ctk?: string;
  jobKey?: string;
  mobtk?: string;
  isError?: boolean;
}): void {
  const { employerOutreachSource, accountKey, ctk, jobKey, mobtk, isError = false } = params;

  logEmployerOutreachInteraction({
    employerOutreachAction: { EmployerOutreachAction: EmployerOutreachAction.COMPLETE_APPLY },
    employerOutreachSource: mapOutreachSourceToEnum(employerOutreachSource),
    accountKey: AvroOptional.string(accountKey),
    ctk: AvroOptional.string(ctk),
    jobkKey: AvroOptional.string(jobKey),
    mobtk: AvroOptional.string(mobtk),
    isError: AvroOptional.boolean(isError),
  });
}
